import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import { UserContext } from "../../App";
import { useCompanyOptions } from "../../utils/company";
import Select from "react-select";
import { createDriver, deleteDriver, updateDriver } from "../../utils/driver";
import { db, apiRoot } from "../../index";
import { FooterActionsContainer } from "../layout/Footer";
import { OutlineButton } from "../layout/OutlineButton";
import axios from "axios";
import moment from "moment-timezone";

export default function DriverAddOrUpdateContent() {
  const history = useHistory();
  const params = useParams();
  const driverId = params.driver_id;

  const { userInfos } = useContext(UserContext);

  const companyOptions = useCompanyOptions();
  const [isFetching, setIsFetching] = useState(false);
  const [formError, setFormError] = useState([]);
  const [driver, setDriver] = useState({
    name: "",
    phone: "",
    email: "",
    internal: false,
    company: {
      name: "",
      id: "",
    },
  });

  // Calculate first and last day of the previous month
  const getLastMonthDates = () => {
    const firstDay = moment().subtract(1, "months").startOf("month");
    const lastDay = moment().subtract(1, "months").endOf("month");

    return {
      startDate: firstDay.format("YYYY-MM-DD"),
      endDate: lastDay.format("YYYY-MM-DD"),
    };
  };

  const [exportDateRange, setExportDateRange] = useState(getLastMonthDates());

  const [isExporting, setIsExporting] = useState(false);

  const handleExportWorkingTime = async () => {
    if (!driverId || !exportDateRange.startDate || !exportDateRange.endDate) {
      return;
    }

    setIsExporting(true);
    try {
      const response = await axios.post(
        `${apiRoot}/api/v1/driver/export-working-time`,
        {
          driverId,
          startDate: exportDateRange.startDate,
          endDate: exportDateRange.endDate,
        },
        {
          headers: {
            authorization: userInfos.jwt,
          },
          responseType: "blob",
        }
      );

      // Create a download link for the Excel file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `heures-travail-${driver.name}-${exportDateRange.startDate}-${exportDateRange.endDate}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error exporting working time:", error);
      window.alert(
        "Une erreur est survenue lors de l'exportation des heures de travail"
      );
    } finally {
      setIsExporting(false);
    }
  };

  useEffect(() => {
    if (driverId && userInfos?.uid) {
      db.ref(`users/${userInfos.uid}/driver/${driverId}`)
        .once("value", (snapshot) => {
          const res = snapshot.val();
          setDriver((old) => ({ ...old, ...res, id: snapshot.key }));
        })
        .then();
    }
  }, [driverId, userInfos?.uid]);

  function handleChange(e) {
    const { name, value, type, checked } = e.target;
    setDriver((old) => ({
      ...old,
      [name]: type === "checkbox" ? checked : value,
    }));
  }

  async function handleDeleteDriver() {
    await deleteDriver(userInfos, driverId);
    history.push("/driver");
  }

  async function handleFormSubmit(e) {
    e.preventDefault();
    if (handleValidation()) {
      setIsFetching(true);

      if (driverId) {
        await updateDriver(userInfos, driver);
        window.alert("Le chauffeur a été mis à jour avec succès");
      } else {
        await createDriver(userInfos, driver);
        history.push("/driver");
      }

      setIsFetching(false);
    }
  }

  function handleValidation() {
    let valid = true;
    let message = [];

    if (driver.name === "") {
      valid = false;
      message.push("Veuillez renseigner le nom du chauffeur");
    }

    if (driver.phone === "") {
      valid = false;
      message.push("Veuillez renseigner le numéro de téléphone du chauffeur");
    }

    if (driver.company.id === "" && driver.company.name === "") {
      valid = false;
      message.push("Veuillez renseigner la compagnie du chauffeur");
    }

    setFormError(message);

    return valid;
  }

  const FormAlert = useMemo(() => {
    const alert =
      formError.length > 0 ? (
        <div
          className="alert alert-danger"
          role="alert"
          style={{ flexDirection: "column" }}
        >
          {formError.map((error, index) => {
            return (
              <div
                className="alert-text"
                key={index}
                style={{ alignSelf: "flex-start" }}
              >
                {error}
              </div>
            );
          })}
        </div>
      ) : (
        ""
      );

    return (
      <div className="kt-container  kt-container--fluid  kt-grid__item kt-grid__item--fluid">
        {alert}
      </div>
    );
  }, [formError]);

  function handleCompanyChange(obj) {
    setDriver((old) => ({
      ...old,
      company: {
        ...obj.data,
        key: obj.data.id,
      },
    }));
  }

  const buttonText =
    isFetching === true ? (
      <button type="submit" className="btn btn-primary px-5" disabled>
        {driverId ? "Mise à jour..." : "Création..."}
      </button>
    ) : (
      <button type="submit" className="btn btn-primary px-5">
        {driverId ? "Mettre à jour le chauffeur" : "Créer le chauffeur"}
      </button>
    );

  return (
    <div
      className="kt-content  kt-grid__item kt-grid__item--fluid kt-grid kt-grid--hor"
      id="kt_content"
    >
      <div className="kt-subheader   kt-grid__item" id="kt_subheader">
        <div className="kt-container  kt-container--fluid ">
          <div className="kt-subheader__main">
            <h3 className="kt-subheader__title">
              {driverId ? driver?.name : "Ajouter un chauffeur"}{" "}
            </h3>
            <div className="kt-subheader__breadcrumbs">
              <Link to="/" className="kt-subheader__breadcrumbs-home">
                <i className="flaticon2-shelter"></i>
              </Link>
              <span className="kt-subheader__breadcrumbs-separator"></span>
              <Link to="/driver/" className="kt-subheader__breadcrumbs-link">
                Chauffeurs
              </Link>
              {driverId ? (
                <>
                  <span className="kt-subheader__breadcrumbs-separator"></span>
                  <Link
                    to={"/driver/" + driverId}
                    className="kt-subheader__breadcrumbs-link"
                  >
                    {driver.name}
                  </Link>
                </>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {FormAlert}

      <div className="kt-container  kt-container--fluid  kt-grid__item kt-grid__item--fluid">
        <div className="kt-portlet" style={{ boxShadow: "none" }}>
          <div className="kt-portlet__head">
            <div className="kt-portlet__head-label">
              <h3 className="kt-portlet__head-title">Données du chauffeur</h3>
            </div>
          </div>
          <form onSubmit={handleFormSubmit} className="kt-form">
            <div className="kt-portlet__body">
              <div className="kt-section kt-section--first">
                <div className="kt-section__body">
                  <div className="form-group row">
                    <label className="col-lg-3 col-form-label">Nom:</label>
                    <div className="col-lg-6">
                      <input
                        type="text"
                        className="form-control"
                        name="name"
                        value={driver.name}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                  <div className="form-group row">
                    <label className="col-lg-3 col-form-label">
                      Téléphone:
                    </label>
                    <div className="col-lg-6">
                      <input
                        type="tel"
                        className="form-control"
                        name="phone"
                        value={driver.phone}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                  <div className="form-group row">
                    <label className="col-lg-3 col-form-label">Email:</label>
                    <div className="col-lg-6">
                      <input
                        type="email"
                        className="form-control"
                        name="email"
                        value={driver.email}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                  <div className="form-group row">
                    <label className="col-lg-3 col-form-label">
                      Chauffeur interne:
                    </label>
                    <div className="col-lg-6">
                      <div className="form-check form-check-inline">
                        <input
                          type="radio"
                          className="form-check-input"
                          id="internalOui"
                          name="internal"
                          value="true"
                          checked={driver.internal === true}
                          onChange={() =>
                            setDriver((old) => ({ ...old, internal: true }))
                          }
                        />
                        <label
                          className="form-check-label"
                          htmlFor="internalOui"
                        >
                          Oui
                        </label>
                      </div>
                      <div className="form-check form-check-inline">
                        <input
                          type="radio"
                          className="form-check-input"
                          id="internalNon"
                          name="internal"
                          value="false"
                          checked={driver.internal === false}
                          onChange={() =>
                            setDriver((old) => ({ ...old, internal: false }))
                          }
                        />
                        <label
                          className="form-check-label"
                          htmlFor="internalNon"
                        >
                          Non
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="form-group row">
                    <label className="col-lg-3 col-form-label">
                      Compagnie:
                    </label>
                    <div className="col-lg-6">
                      <Select
                        options={companyOptions}
                        onChange={handleCompanyChange}
                      />
                    </div>
                  </div>
                  <div className="form-group row">
                    <label className="col-lg-3 col-form-label"></label>
                    <div className="col-lg-6">
                      <input
                        type="text"
                        className="form-control"
                        name="company"
                        value={driver.company.name}
                        disabled
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {driverId && driver.internal && (
              <div className="kt-portlet mt-4" style={{ boxShadow: "none" }}>
                <div className="kt-portlet__head">
                  <div className="kt-portlet__head-label">
                    <h3 className="kt-portlet__head-title">
                      Exporter les heures de travail
                    </h3>
                  </div>
                </div>
                <div className="kt-portlet__body">
                  <div className="kt-section kt-section--first">
                    <div className="kt-section__body">
                      <div className="form-group row">
                        <label className="col-lg-3 col-form-label">
                          Date de début:
                        </label>
                        <div className="col-lg-6">
                          <input
                            type="date"
                            className="form-control"
                            name="startDate"
                            value={exportDateRange.startDate}
                            onChange={(e) =>
                              setExportDateRange((prev) => ({
                                ...prev,
                                startDate: e.target.value,
                              }))
                            }
                          />
                        </div>
                      </div>
                      <div className="form-group row">
                        <label className="col-lg-3 col-form-label">
                          Date de fin:
                        </label>
                        <div className="col-lg-6">
                          <input
                            type="date"
                            className="form-control"
                            name="endDate"
                            value={exportDateRange.endDate}
                            onChange={(e) =>
                              setExportDateRange((prev) => ({
                                ...prev,
                                endDate: e.target.value,
                              }))
                            }
                          />
                        </div>
                      </div>
                      <div className="form-group row">
                        <div className="col-lg-3"></div>
                        <div className="col-lg-6">
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleExportWorkingTime}
                            disabled={
                              isExporting ||
                              !exportDateRange.startDate ||
                              !exportDateRange.endDate
                            }
                          >
                            {isExporting
                              ? "Exportation en cours..."
                              : "Exporter"}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <FooterActionsContainer>
              <div>
                <OutlineButton type="button" onClick={history.goBack}>
                  <i className="fas fa-arrow-left" />
                  Retour
                </OutlineButton>
              </div>
              <div>{buttonText}</div>
              <div>
                {driverId && (
                  <OutlineButton
                    type="button"
                    data-toggle="modal"
                    data-target="#delete_modal"
                  >
                    <i className="fas fa-trash" />
                    Supprimer
                  </OutlineButton>
                )}
              </div>
            </FooterActionsContainer>
          </form>
        </div>
      </div>
      <div
        className="modal fade"
        id="delete_modal"
        tabIndex="-1"
        role="dialog"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="exampleModalLongTitle">
                Supprimer le chauffeur
              </h5>
              <button
                type="button"
                className="close"
                data-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <p>Êtes-vous sûr de vouloir supprimer ce chauffeur?</p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                data-dismiss="modal"
              >
                Annuler
              </button>
              <button
                type="button"
                className="btn btn-primary"
                data-dismiss="modal"
                onClick={handleDeleteDriver}
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
